import type { SiteData } from '../../shared/types/site.js';
import siteOverrides from '../../../data/site.json';

const baseSiteData: SiteData = {
  hero: {
    line1: 'We design',
    line2: 'and build',
    line3: 'digital advantage.',
    tagline:
      'From strategy to launch, we build digital products that feel clear, fast, and reliable.',
  },
  services: [
    {
      id: '1',
      title: 'Web Development',
      description:
        'High-performance web platforms built to scale, convert, and represent your brand with clarity.',
      slug: 'web-development',
      icon: 'web',
    },
    {
      id: '2',
      title: 'Mobile App',
      description:
        'Mobile experiences that are fast, intuitive, and refined across iOS and Android.',
      slug: 'mobile-app',
      icon: 'mobile',
    },
    {
      id: '3',
      title: 'Creative Design (UI/UX)',
      description:
        'Human-centered design systems that balance beauty, usability, and business outcomes.',
      slug: 'creative-design-uiux',
      icon: 'design',
    },
    {
      id: '4',
      title: 'Consulting',
      description:
        'Clear technical direction, execution planning, and decision support for leadership teams.',
      slug: 'consulting',
      icon: 'consulting',
    },
    {
      id: '5',
      title: 'Maintenance and Support',
      description:
        'Proactive care to keep your product secure, stable, and always improving.',
      slug: 'maintenance-and-support',
      icon: 'support',
    },
    {
      id: '6',
      title: 'AI futuristic',
      description:
        'Practical AI that automates workflows, unlocks insight, and delivers real ROI.',
      slug: 'ai-futuristic',
      icon: 'ai',
    },
    {
      id: '7',
      title: 'Game Development',
      description:
        'Game experiences and interactive systems designed for engagement, performance, and growth.',
      slug: 'game-development',
      icon: 'game',
    },
    {
      id: '8',
      title: 'Original Products & Solutions',
      description:
        'From concept to launch, we develop original digital products and tailored solutions with clear market fit.',
      slug: 'original-products-solutions',
      icon: 'product',
    },
  ],
  ourWork: {
    title: 'Our Signature Work',
    intro:
      'A selection of fields that blend strategy, execution, and measurable impact.',
    projects: [
      {
        id: '1',
        category: 'Web & Mobile Systems',
        name: 'ARKRON Platform',
        description:
          'A modular platform built for speed, clarity, and long-term growth.',
        slug: 'arkron-platform',
        imageUrl:
          'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
      },
      {
        id: '2',
        category: 'Native Apps',
        name: 'Native Experience',
        description:
          'Mobile experiences with performance, polish, and purpose.',
        slug: 'native-experience',
        imageUrl:
          'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=800&q=80',
      },
      {
        id: '3',
        category: 'AI Solutions',
        name: 'Smart Systems',
        description:
          'Intelligent features embedded where they drive meaningful outcomes.',
        slug: 'smart-systems',
        imageUrl:
          'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80',
      },
    ],
  },
  industries: {
    title: 'Industries We Know Well',
    intro:
      'We partner with teams across sectors to deliver tailored digital transformation.',
    items: [
      {
        id: '1',
        title: 'AI futuristic & ML',
        description:
          'Applied ML solutions that automate, optimize, and scale operations.',
        slug: 'ai-futuristic-ml',
      },
      {
        id: '2',
        title: 'Agriculture',
        description:
          'Precision tools for farming, supply chain visibility, and agri-tech growth.',
        slug: 'agriculture',
      },
      {
        id: '3',
        title: 'Real Estate',
        description:
          'Platforms for listings, property ops, and modern client experiences.',
        slug: 'real-estate',
      },
      {
        id: '4',
        title: 'Social Networking',
        description:
          'Community and engagement platforms built for scale and trust.',
        slug: 'social-networking',
      },
      {
        id: '5',
        title: 'Retail & E-commerce',
        description:
          'Commerce experiences that increase conversion and customer loyalty.',
        slug: 'retail-ecommerce',
      },
      {
        id: '6',
        title: 'Gaming',
        description:
          'Player-focused products, game systems, and live experiences built for retention and scale.',
        slug: 'gaming',
      },
    ],
  },
  company: {
    title: 'Who we are',
    body:
      'ARKRON is a product studio helping ambitious teams build bold, reliable software.',
    readMoreLink: '/about-us',
  },
  contact: {
    titleLine2: 'collaborate',
    subtitle: 'We\'re ready to hear your ideas and help bring them to life.',
    cta: 'Contact Us',
    email: 'hello@arkron.al',
  },
  footer: { text: '© 2026 ARKRON - Above the sky, beneath the lands' },
  technologies: {
    title: 'Tools chosen with intent',
    intro:
      'We select the right technologies for the challenge, then execute with speed, quality, and care.',
    categories: [
      {
        id: '4',
        name: 'Mobile',
        items: [
          { id: '1', name: 'Swift', logoUrl: 'https://cdn.simpleicons.org/swift/F05138' },
          {
            id: '2',
            name: 'SwiftUI',
            logoUrl:
              'https://cdn.simpleicons.org/swift/0A84FF',
          },
          { id: '3', name: 'Kotlin', logoUrl: 'https://cdn.simpleicons.org/kotlin/7F52FF' },
          {
            id: '4',
            name: 'Jetpack Compose',
            logoUrl: 'https://cdn.simpleicons.org/jetpackcompose/4285F4',
          },
        ],
      },
      {
        id: '2',
        name: 'Frontend',
        items: [
          {
            id: '1',
            name: 'TypeScript',
            logoUrl: 'https://cdn.simpleicons.org/typescript/3178C6',
          },
          { id: '2', name: 'Astro', logoUrl: 'https://cdn.simpleicons.org/astro/FF5D01' },
          {
            id: '3',
            name: 'Tailwind CSS',
            logoUrl: 'https://cdn.simpleicons.org/tailwindcss/06B6D4',
          },
        ],
      },
      {
        id: '1',
        name: 'Backend',
        items: [
          { id: '1', name: 'Vapor (Swift)', logoUrl: 'https://cdn.simpleicons.org/vapor/0D0D0D' },
          { id: '2', name: 'Ktor (Kotlin)', logoUrl: 'https://cdn.simpleicons.org/ktor/087CFA' },
        ],
      },
      {
        id: '7',
        name: 'UI/UX',
        items: [
          { id: '1', name: 'Figma', logoUrl: 'https://cdn.simpleicons.org/figma/F24E1E' },
          { id: '2', name: 'Sketch', logoUrl: 'https://cdn.simpleicons.org/sketch/F7B500' },
          {
            id: '3',
            name: 'DaVinci Resolve',
            logoUrl: 'https://cdn.simpleicons.org/davinciresolve/233A51',
          },
          { id: '4', name: 'CapCut', logoUrl: 'data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A//www.w3.org/2000/svg%27%20viewBox%3D%270%200%2064%2064%27%3E%3Crect%20width%3D%2764%27%20height%3D%2764%27%20rx%3D%2710%27%20fill%3D%27white%27/%3E%3Cpath%20d%3D%27M16%2022h10l22%2020H38zM16%2042h10l22-20H38zM16%2032h32%27%20stroke%3D%27%2311141a%27%20stroke-width%3D%274%27%20stroke-linecap%3D%27round%27%20stroke-linejoin%3D%27round%27%20fill%3D%27none%27/%3E%3C/svg%3E' },
        ],
      },
      {
        id: '3',
        name: 'Cloud',
        items: [
          {
            id: '1',
            name: 'Scaleway',
            logoUrl: 'https://cdn.simpleicons.org/scaleway/4F0599',
          },
          {
            id: '2',
            name: 'Backblaze B2',
            logoUrl: 'https://cdn.simpleicons.org/backblaze/FF2A1A',
          },
          { id: '3', name: 'Hetzner', logoUrl: 'https://cdn.simpleicons.org/hetzner/D50C2D' },
          { id: '4', name: 'Cloudflare', logoUrl: 'https://cdn.simpleicons.org/cloudflare/F38020' },
        ],
      },
      {
        id: '5',
        name: 'Databases',
        items: [
          { id: '1', name: 'PostgreSQL', logoUrl: 'https://cdn.simpleicons.org/postgresql/4169E1' },
          { id: '2', name: 'GRDB', logoUrl: 'https://cdn.simpleicons.org/sqlite/003B57' },
          { id: '3', name: 'SwiftData', logoUrl: 'https://cdn.simpleicons.org/swift/F05138' },
          { id: '4', name: 'Room', logoUrl: 'https://cdn.simpleicons.org/kotlin/7F52FF' },
        ],
      },
      {
        id: '6',
        name: 'Product',
        items: [
          {
            id: '1',
            name: 'Slack',
            logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/d/d5/Slack_icon_2019.svg',
          },
          { id: '2', name: 'Linear', logoUrl: 'https://cdn.simpleicons.org/linear/5E6AD2' },
          { id: '3', name: 'Penpot', logoUrl: 'https://cdn.simpleicons.org/penpot/000000' },
          { id: '5', name: 'GitHub', logoUrl: 'https://cdn.simpleicons.org/github/181717' },
          { id: '9', name: 'PostHog', logoUrl: 'https://cdn.simpleicons.org/posthog/F54E00' },
          { id: '10', name: 'Yaak', logoUrl: 'https://yaak.app/static/logo.svg' },
          { id: '11', name: 'Beekeeper Studio', logoUrl: 'https://cdn.simpleicons.org/beekeeperstudio/F7B500' },
          { id: '12', name: 'Fork', logoUrl: 'https://git-fork.com/favicon.ico' },
        ],
      },
      {
        id: '9',
        name: 'DevOps',
        items: [
          { id: '1', name: 'Docker', logoUrl: 'https://cdn.simpleicons.org/docker/2496ED' },
          { id: '2', name: 'Bugsink', logoUrl: 'https://bugsink.com/favicon.ico' },
          { id: '3', name: 'Prometheus / Grafana', logoUrl: 'https://cdn.simpleicons.org/grafana/F46800' },
          { id: '4', name: 'Backrest', logoUrl: 'https://garethgeorge.github.io/backrest/favicon.ico' },
          { id: '5', name: 'Caddy', logoUrl: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%3E%3Ccircle%20cx%3D%2212%22%20cy%3D%2212%22%20r%3D%2210%22%20fill%3D%22%231F88C0%22/%3E%3Crect%20x%3D%227%22%20y%3D%2211%22%20width%3D%2210%22%20height%3D%228%22%20rx%3D%222%22%20fill%3D%22white%22/%3E%3Cpath%20d%3D%22M9%2011V8a3%203%200%200%201%206%200v3%22%20stroke%3D%22white%22%20stroke-width%3D%222%22%20fill%3D%22none%22%20stroke-linecap%3D%22round%22/%3E%3C/svg%3E' },
          { id: '6', name: 'Kubernetes', logoUrl: 'https://cdn.simpleicons.org/kubernetes/326CE5' },
          { id: '7', name: 'Terraform', logoUrl: 'https://cdn.simpleicons.org/terraform/844FBA' },
        ],
      },
      {
        id: '10',
        name: 'Game Development',
        items: [
          { id: '1', name: 'Unreal Engine', logoUrl: 'https://cdn.simpleicons.org/unrealengine/FFFFFF' },
          { id: '2', name: 'Godot', logoUrl: 'https://cdn.simpleicons.org/godotengine/478CBF' },
          { id: '3', name: 'Blender', logoUrl: 'https://cdn.simpleicons.org/blender/F5792A' },
          { id: '4', name: 'Krita', logoUrl: 'https://cdn.simpleicons.org/krita/3BABFF' },
          { id: '5', name: 'Bitwig', logoUrl: 'data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A//www.w3.org/2000/svg%27%20viewBox%3D%270%200%2064%2064%27%3E%3Crect%20width%3D%2764%27%20height%3D%2764%27%20rx%3D%2710%27%20fill%3D%27white%27/%3E%3Cg%20fill%3D%27%230a0a0a%27%3E%3Ccircle%20cx%3D%2714%27%20cy%3D%2714%27%20r%3D%274%27/%3E%3Ccircle%20cx%3D%2726%27%20cy%3D%2714%27%20r%3D%274%27/%3E%3Ccircle%20cx%3D%2738%27%20cy%3D%2714%27%20r%3D%274%27/%3E%3Ccircle%20cx%3D%2750%27%20cy%3D%2714%27%20r%3D%274%27/%3E%3Ccircle%20cx%3D%278%27%20cy%3D%2726%27%20r%3D%274%27/%3E%3Ccircle%20cx%3D%2720%27%20cy%3D%2726%27%20r%3D%274%27/%3E%3Ccircle%20cx%3D%2732%27%20cy%3D%2726%27%20r%3D%274%27/%3E%3Ccircle%20cx%3D%2744%27%20cy%3D%2726%27%20r%3D%274%27/%3E%3Ccircle%20cx%3D%2756%27%20cy%3D%2726%27%20r%3D%274%27/%3E%3Ccircle%20cx%3D%2714%27%20cy%3D%2738%27%20r%3D%274%27/%3E%3Ccircle%20cx%3D%2726%27%20cy%3D%2738%27%20r%3D%274%27/%3E%3Ccircle%20cx%3D%2738%27%20cy%3D%2738%27%20r%3D%274%27/%3E%3Ccircle%20cx%3D%2750%27%20cy%3D%2738%27%20r%3D%274%27/%3E%3C/g%3E%3Ctext%20x%3D%2732%27%20y%3D%2757%27%20text-anchor%3D%27middle%27%20font-family%3D%27Arial%2Csans-serif%27%20font-size%3D%279%27%20font-weight%3D%27700%27%20fill%3D%27%230a0a0a%27%3EBITWIG%3C/text%3E%3C/svg%3E' },
          { id: '6', name: 'ElevenLabs', logoUrl: 'https://elevenlabs.io/favicon.ico' },
          { id: '7', name: 'Meshy', logoUrl: 'https://www.meshy.ai/favicon.ico' },
          { id: '8', name: 'Mixamo', logoUrl: 'https://www.mixamo.com/favicon.ico' },
        ],
      },
    ],
  },
};

export const defaultSiteData: SiteData = {
  ...baseSiteData,
  ...siteOverrides,
  technologies: siteOverrides.technologies ?? baseSiteData.technologies,
};
